import type { CustomerOrder } from '../../customerOrdersApi'
import type { ShopLanguage, ShopLocale, ShopMessageKey } from '../../../i18n'
import type { ActiveFilterChip, FilteredOrderRow } from './ordersListSectionTypes'
import type {
  OrderDateFilterOption,
  OrderPageSizeOption,
  OrderSortOption,
  OrderVisualStatus,
} from './ordersListShared'
import type { SelectOption } from '../../../components/SelectCustom'
import { OrdersResultsSection } from './OrdersResultsSection'
import { OrdersToolbarSection } from './OrdersToolbarSection'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type OrdersManagementPanelProps = {
  reducedMotion: boolean
  loading: boolean
  ordersError: string
  orders: CustomerOrder[]
  filteredOrders: FilteredOrderRow[]
  paginatedOrders: FilteredOrderRow[]
  pagination: { from: number; to: number }
  pageSize: OrderPageSizeOption
  currentPage: number
  totalPages: number
  visiblePages: number[]
  pageSizeOptions: SelectOption[]
  searchValue: string
  sortBy: OrderSortOption
  filtersOpen: boolean
  dateFilter: OrderDateFilterOption
  customDateFrom: string
  customDateTo: string
  statusFilters: OrderVisualStatus[]
  priceMin: string
  priceMax: string
  sortOptions: SelectOption[]
  dateFilterOptions: SelectOption[]
  activeFilterChips: ActiveFilterChip[]
  locale: ShopLocale
  language: ShopLanguage
  dateFormatter: Intl.DateTimeFormat
  onSearchChange: (value: string) => void
  onToggleFilters: () => void
  onSortChange: (value: OrderSortOption) => void
  onDateFilterChange: (value: OrderDateFilterOption) => void
  onCustomDateFromChange: (value: string) => void
  onCustomDateToChange: (value: string) => void
  onToggleStatusFilter: (status: OrderVisualStatus, checked: boolean) => void
  onPriceMinChange: (value: string) => void
  onPriceMaxChange: (value: string) => void
  onClearAllFilters: () => void
  onOpenOrderDetail: (orderId: string) => void
  onBuyAgain: (order: CustomerOrder) => void
  onRequestSupport: (order: CustomerOrder) => void
  onOpenCatalog: () => void
  onSetPageSize: (size: OrderPageSizeOption) => void
  onSetCurrentPage: (page: number) => void
  t: TranslateFn
}

export const OrdersManagementPanel = ({
  reducedMotion,
  loading,
  ordersError,
  orders,
  filteredOrders,
  paginatedOrders,
  pagination,
  pageSize,
  currentPage,
  totalPages,
  visiblePages,
  pageSizeOptions,
  searchValue,
  sortBy,
  filtersOpen,
  dateFilter,
  customDateFrom,
  customDateTo,
  statusFilters,
  priceMin,
  priceMax,
  sortOptions,
  dateFilterOptions,
  activeFilterChips,
  locale,
  language,
  dateFormatter,
  onSearchChange,
  onToggleFilters,
  onSortChange,
  onDateFilterChange,
  onCustomDateFromChange,
  onCustomDateToChange,
  onToggleStatusFilter,
  onPriceMinChange,
  onPriceMaxChange,
  onClearAllFilters,
  onOpenOrderDetail,
  onBuyAgain,
  onRequestSupport,
  onOpenCatalog,
  onSetPageSize,
  onSetCurrentPage,
  t,
}: OrdersManagementPanelProps) => (
  <section className="ca-theme-transition overflow-hidden rounded-xl border border-(--color-border-default) bg-(--color-bg-card) shadow-(--shadow-sm) lg:min-h-[26rem] xl:min-h-[27rem]">
    <OrdersToolbarSection
      reducedMotion={reducedMotion}
      loading={loading}
      language={language}
      searchValue={searchValue}
      sortBy={sortBy}
      filtersOpen={filtersOpen}
      dateFilter={dateFilter}
      customDateFrom={customDateFrom}
      customDateTo={customDateTo}
      statusFilters={statusFilters}
      priceMin={priceMin}
      priceMax={priceMax}
      sortOptions={sortOptions}
      dateFilterOptions={dateFilterOptions}
      activeFilterChips={activeFilterChips}
      filteredCount={filteredOrders.length}
      onSearchChange={onSearchChange}
      onToggleFilters={onToggleFilters}
      onSortChange={onSortChange}
      onDateFilterChange={onDateFilterChange}
      onCustomDateFromChange={onCustomDateFromChange}
      onCustomDateToChange={onCustomDateToChange}
      onToggleStatusFilter={onToggleStatusFilter}
      onPriceMinChange={onPriceMinChange}
      onPriceMaxChange={onPriceMaxChange}
      onClearAllFilters={onClearAllFilters}
      t={t}
    />

    <OrdersResultsSection
      reducedMotion={reducedMotion}
      loading={loading}
      ordersError={ordersError}
      searchValue={searchValue}
      sortBy={sortBy}
      activeFilterChipsLength={activeFilterChips.length}
      orders={orders}
      filteredOrders={filteredOrders}
      paginatedOrders={paginatedOrders}
      currentPage={currentPage}
      totalPages={totalPages}
      visiblePages={visiblePages}
      pageSize={pageSize}
      pageSizeOptions={pageSizeOptions}
      pagination={pagination}
      locale={locale}
      language={language}
      dateFormatter={dateFormatter}
      onOpenOrderDetail={onOpenOrderDetail}
      onBuyAgain={onBuyAgain}
      onRequestSupport={onRequestSupport}
      onOpenCatalog={onOpenCatalog}
      onClearAllFilters={onClearAllFilters}
      onSetPageSize={onSetPageSize}
      onSetCurrentPage={onSetCurrentPage}
      t={t}
    />
  </section>
)
